import { useQuery, useLazyQuery, gql } from '@apollo/react-hooks';
import type { GetConditionMeetVariables } from './types';
import { getMeetConditionStatus } from '../../../../Application/components/ApplicationStatus/Offer/ConditionMeet/meetConditionFunctions';

const GET_CONDITION_MEET = gql`
  query ($id: ID!, $applicationId: String!) {
    application(id: $id) {
      programmeChoice {
        entryYear
      }
      academicQualifications {
        _id
        presentIndicator
        gpaIndicator
        rankIndicator
        academicCredentialIndicator
        degreeCertIndicator
        from
        to
        studyMode
        institution {
          country
          name
        }
        instructionMedium
        otherInstructionMedium
        currentStudyYear
        studyField
        degreeLevel
        qualificationMeans
        programmeName
        qualificationStatus
        academicOnlineVerificationCode
        degreeOnlineVerificationCode
        expectUpdateDate
        awardDate
        awardClassification
        withdrawDate
        officialClassRanking
        outOfRank
        officialMark
        outOfGpa
        convertGPARuleId
        marks {
          course
          credit
          result
        }
        markConversion {
          convertedMark
          letterToGPARule {
            name
          }
          digitToLetterRule {
            name
          }
        }

        certificates {
          id
          link
          name
        }

        transcripts {
          id
          link
          name
        }

        markExplanations {
          id
          link
          name
        }

        classRankProofs {
          id
          link
          name
        }

        otherDocuments {
          id
          link
          name
        }
      }
      languageQualifications {
        _id
        needEnglishTest
        testType
        testDate
        referenceNo
        expectedResultUpdateDate
        resultAnnouncedIndicator
        result {
          resultReceiptDate
          listening
          reading
          writing
          speaking
          overall
        }
        supportingDocuments {
          id
          link
          name
        }
      }

      otherTests {
        testType
        referenceNo
        testDate
        expectedResultUpdateDate
        resultAnnouncedIndicator
        result {
          totalScore
          totalPercentile
          quantitativeScore
          quantitativePercentile
          analyticalWritingScore
          analyticalWritingPercentile
          verbalScore
          verbalPercentile
          integratedReasoningScore
          integratedReasoningPercentile
          dataInsightsScore
          dataInsightsPercentile
        }
        supportingDocuments {
          id
          link
          name
        }
        _id
      }
    }

    meetCondition(applicationId: $applicationId) {
      conditions {
        item
        passed
        remark
      }
      conditionFulfillingDeadline
      contactDepartmentPersonName
      contactDepartmentPersonEmail
      contactDepartmentPersonPhone
      status
    }
  }
`;

const GET_REGISTRATION_DEADLINE = gql`
  query ($entryYear: String!) {
    entryYearInformation(entryYear: $entryYear) {
      registrationDeadline
    }
  }
`;

export const useGetConditionMeet = (
  applicationId: string,
): GetConditionMeetVariables => {
  const [
    getRegistrationDeadline,
    { data: deadlineData, loading: deadlineLoading, error: deadlineError },
  ] = useLazyQuery(GET_REGISTRATION_DEADLINE);

  const { data, loading, error, refetch } = useQuery(GET_CONDITION_MEET, {
    variables: { id: applicationId, applicationId },
    onCompleted: async data => {
      const entryYear = data?.application?.programmeChoice?.entryYear;
      const isValidEntryYear = Boolean(entryYear);
      if (isValidEntryYear) {
        await getRegistrationDeadline({ variables: { entryYear } });
      }
    },
  });

  const {
    conditions,
    conditionFulfillingDeadline,
    contactDepartmentPersonName,
    contactDepartmentPersonEmail,
    contactDepartmentPersonPhone,
    status,
  } = data?.meetCondition ?? {};
  const application = data?.application ?? {};

  const registrationDeadline =
    deadlineData?.entryYearInformation?.registrationDeadline ?? '';

  const fullStatus = getMeetConditionStatus(
    status,
    conditionFulfillingDeadline,
  );

  return {
    conditionMeet: {
      conditions: conditions ?? [],
      conditionFulfillingDeadline,
      contactDepartmentPersonName,
      contactDepartmentPersonEmail,
      contactDepartmentPersonPhone,
      academicQualifications: application?.academicQualifications ?? [],
      languageQualifications: application?.languageQualifications ?? [],
      otherTests: application?.otherTests ?? [],
      status: fullStatus,
    },
    registrationDeadline,
    loading: loading || deadlineLoading,
    error: error ?? deadlineError,
    refetch,
  };
};
